(function () {
  const SECTION_CLASS = "tabs-slider-section";

  const initSection = (section) => {
    if (!section || !section.classList.contains(SECTION_CLASS)) return;
    if (typeof Swiper === "undefined") return;

    const root = section.querySelector(".tabs-slider");
    if (!root) return;
    const mediaEl = root.querySelector(".tabs-slider__media-slider");
    const thumbsEl = root.querySelector(".tabs-slider__tabs");
    if (!mediaEl || !thumbsEl) return;

    // The structure is server-rendered (two .swiper blocks). The theme editor
    // reloads a section by BOTH re-running this inline script AND firing
    // shopify:section:load — bail on the second call (already a live Swiper).
    if (mediaEl.swiper) return;

    if (section._tabsSliderCleanup) {
      section._tabsSliderCleanup();
      section._tabsSliderCleanup = null;
    }

    const count = mediaEl.querySelectorAll(".tabs-slider__media-slide").length;
    if (count === 0) return;

    const autoplay = root.dataset.autoplay === "true" && count > 1;
    const pauseOnHover = root.dataset.autoplayPauseOnHover === "true";
    const delay = parseInt(root.dataset.autoplayDelay, 10) || 5000;

    const tabButtons = Array.from(
      thumbsEl.querySelectorAll(".tabs-slider__tab")
    );
    const progressBars = Array.from(
      thumbsEl.querySelectorAll(".tabs-slider__progress-bar")
    );

    // The heading rolls a copy of itself into place on activation, and that copy is
    // ::before content — so the text has to reach CSS as an attribute. Richtext
    // markup inside the heading flattens to text here, same as .header-roll.
    thumbsEl
      .querySelectorAll(".tabs-slider__tab-heading .block-heading")
      .forEach((heading) => {
        heading.dataset.text = heading.textContent.trim();
      });

    // Bars are in slide order, so the active one is progressBars[realIndex]
    // (no loop → realIndex === DOM index). --slider-progress (0 empty → 1 full)
    // drives the same translate fill the shared autoplay sliders use (slider.scss).
    const fill = (index, value) => {
      const bar = progressBars[index];
      if (!bar) return;
      // Snapped to whole pixels of the track: a leading edge landing mid-pixel is
      // antialiased, and on a 2px line that half-lit column reads as the fill
      // greying out at its front. Read before the write, so the frame's layout is
      // still clean and this costs nothing.
      const width = bar.offsetWidth;
      const snapped = width ? Math.round(value * width) / width : value;
      bar.style.setProperty("--slider-progress", String(snapped));
    };

    // Slide-change duration: `speed` below, --tabs-slider-lead and the
    // media-mask-change animation in the SCSS all run on it.
    const SLIDE_SPEED = 1000;
    // Autoplay pauses for the whole slide change and only then restarts its
    // timer, so a switched-to slide dwells speed + delay. The bar covers the
    // change's share of that itself the moment the tab is taken (see handOver),
    // and autoplayTimeLeft carries the rest on from there — same rate, so it
    // reads as one uninterrupted fill.
    const BRIDGE = SLIDE_SPEED / (SLIDE_SPEED + delay);
    let activeBar = null;
    // Where the countdown fill starts from, the value last painted, and how far
    // through its delay the timer was on that frame — the three the fill needs to
    // carry on across a restart instead of rewinding (see autoplayTimeLeft).
    let fillBase = 0;
    let fillValue = 0;
    let lastTimerFraction = 0;
    const rebaseFill = (value) => {
      fillBase = value;
      fillValue = value;
      lastTimerFraction = 0;
    };
    // Non-null while the bar runs its own stretch — the bridged fill — and the gate
    // autoplayTimeLeft is held behind for it.
    let holdTimer = null;
    // Empty again in a single frame: the classes carry the only transition the bar
    // has, so dropping them clears the fill outright instead of easing it out — the
    // tab handing over simply loses its line as the hovered one starts loading.
    const clearBar = (bar) => {
      bar.classList.remove("is-filling", "is-easing");
      bar.style.setProperty("--slider-progress", "0");
    };

    // Every bar loads inside its OWN track, growing from the edge the move comes
    // from — nothing travels between the tabs.
    const handOver = (index) => {
      // Whatever stretch the last bar was running is over — dropped before anything
      // else, or its timer would land on a bar that has moved on since.
      clearTimeout(holdTimer);
      holdTimer = null;
      const bar = progressBars[index] || null;
      if (bar === activeBar) return;
      const leaving = activeBar;
      activeBar = bar;

      if (leaving) clearBar(leaving);
      if (!bar) return;

      // Emptied while the bar carries no transition, so the reflow commits it —
      // otherwise a bar hovered back mid-fill eases down from where it stands
      // instead of growing from the leading edge.
      clearBar(bar);
      void bar.offsetWidth;

      if (autoplay) {
        rebaseFill(0);
        // Nothing hands over on the first sync — that slide dwells the plain delay,
        // so its bar fills from empty and needs no bridge.
        if (!leaving) {
          fill(index, 0);
          return;
        }

        rebaseFill(BRIDGE);
        bar.classList.add("is-filling");
        fill(index, fillBase);
        // autoplayTimeLeft emits a last frame of the old countdown before Swiper
        // pauses it for the change, and its value is where the bridge is headed
        // — applying it would drop the transition and snap the bar there. So the
        // bridge owns the bar until it lands, then hands it straight over: by
        // then the timer has restarted, so the first frame through carries the
        // same value the bridge stopped on.
        holdTimer = setTimeout(() => {
          holdTimer = null;
          bar.classList.remove("is-filling");
        }, SLIDE_SPEED);
        return;
      }

      // Autoplay off: nothing drives the fill frame by frame, so the bar loads its
      // track in one ease. Nothing hands over on the first sync, so the tab the
      // section opens on is static.
      if (leaving) bar.classList.add("is-easing");
      fill(index, 1);
    };

    const syncTabs = (swiper) => {
      tabButtons.forEach((btn, i) => {
        btn.setAttribute(
          "aria-pressed",
          i === swiper.realIndex ? "true" : "false"
        );
      });
      revealTab(swiper.realIndex);
      handOver(swiper.realIndex);
    };

    // Creative stacking + reveal are driven ONLY from transition start. The creative
    // effect is virtual-translate: Swiper detects the transition end via the
    // slides' opacity transitionend, so a fast tab-to-tab hover lets the
    // INTERRUPTED transition's transitionend fire right after the new change
    // starts — any cleanup hooked to slideChangeTransitionEnd would strip
    // is-revealing off the slide that just began opening and its animation would
    // never visibly play. is-revealing staying on a resting slide is inert
    // (both-fill = the whole frame), so no end-of-transition restore is needed.
    //
    // Each slide owns its own keyframe instance, so a reveal cut short by the next
    // tab is LEFT RUNNING and opens on beneath the new one — resetting it here
    // snapped it to the whole frame in a single frame, which is the jump a pointer
    // sweeping across the tab row used to flash. Only the incoming slide is reset,
    // so only its keyframe restarts.
    //
    // Slides are layered by how recently they were activated: newest (narrowest
    // window) on top, older (further along, so wider) below — so the opening
    // windows nest instead of clipping each other.
    const revealStack = [];

    // Only the newest pair may run at once: with a full-size slide under them the
    // frame stays covered, but a third overlapping reveal leaves the bottom layer
    // part-way and the section shows through around it. That layer is eased the
    // rest of the way instead — same speed as the reveal it replaces, so it reads
    // as a settle, not the one-frame snap that resetting a running reveal produced.
    const SETTLE_RATE = 400;
    // Where media-mask-change (main.scss) starts its inset, so how far a mask has
    // left to open reads as a share of the whole travel.
    const MASK_START = 45;
    const clearSettle = (box) => {
      box.style.transition = "";
      box.style.clipPath = "";
    };
    const settleReveal = (slide) => {
      const box = slide.querySelector(".tabs-slider__media");
      if (!box) return;
      // Where the mask stands right now: the computed value carries the running
      // keyframe, e.g. "inset(17.4% round 24px)", and its first number is the inset.
      const clip = getComputedStyle(box).clipPath;
      const inset =
        clip.indexOf("inset(") === 0 ? parseFloat(clip.slice(6)) : 0;
      slide.classList.remove("is-revealing");
      if (!inset) {
        clearSettle(box);
        return;
      }
      const ms = Math.max(80, Math.round((inset / MASK_START) * SETTLE_RATE));
      box.style.transition = "none";
      box.style.clipPath = clip;
      void box.offsetWidth;
      box.style.transition =
        "clip-path " + ms + "ms cubic-bezier(0.4, 0, 0.2, 1)";
      box.style.clipPath = clip.replace(/[\d.]+%/, "0%");
    };

    const startCreativeReveal = (swiper) => {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (!activeSlide) return;

      const stacked = revealStack.indexOf(activeSlide);
      if (stacked !== -1) revealStack.splice(stacked, 1);
      revealStack.unshift(activeSlide);

      const topZ = swiper.slides.length + 2;
      swiper.slides.forEach((slide) => {
        slide.style.zIndex = "1";
      });
      revealStack.forEach((slide, index) => {
        slide.style.zIndex = String(topZ - index);
      });
      revealStack.splice(2).forEach(settleReveal);

      // Force a reflow between remove + add — a same-frame re-add never restarts
      // the keyframe, and the incoming slide may still carry is-revealing after a
      // fast hover back onto it.
      const activeBox = activeSlide.querySelector(".tabs-slider__media");
      if (activeBox) clearSettle(activeBox);
      activeSlide.classList.remove("is-revealing");
      void activeSlide.offsetWidth;
      activeSlide.classList.add("is-revealing");
    };

    // Tabs are a real horizontal thumbs slider: on mobile the active tab fills the
    // row with the next peeking, the rest off-screen and swipeable; from $md up all
    // tabs fit in the row (slidesPerView = count) so nothing scrolls.
    const MD = 750;
    const thumbsSwiper = new Swiper(thumbsEl, {
      slidesPerView: 1.15,
      // Keep in sync with the pre-init gap in the SCSS.
      spaceBetween: 24,
      watchSlidesProgress: true,
      a11y: { enabled: false },
      breakpoints: {
        [MD]: {
          slidesPerView: count,
        },
      },
    });

    // The tab the slider moves to has to come fully into the row. Swiper's own
    // thumbs auto-scroll only steps in when the target sits OUTSIDE
    // visibleSlidesIndexes, and on mobile the next tab always peeks
    // (slidesPerView 1.15) — so it counts as visible and an autoplay step onto it
    // left it a sliver on screen for the whole slide. Fully visible is the state
    // that matters here, and testing for it keeps this a no-op from $md up, where
    // the whole row fits.
    const revealTab = (index) => {
      if (thumbsSwiper.destroyed) return;
      const slide = thumbsSwiper.slides[index];
      if (!slide || slide.classList.contains("swiper-slide-fully-visible")) {
        return;
      }
      thumbsSwiper.slideTo(index);
    };

    const mainSwiper = new Swiper(mediaEl, {
      slidesPerView: 1,
      // Slides are stacked by the creative effect, so no gap between them.
      spaceBetween: 0,
      // Matches the slideshow. Keep in sync with the media-mask-change duration
      // in the SCSS — the reveal has to land exactly as the slide change ends.
      speed: SLIDE_SPEED,
      effect: "creative",
      // The mask opens from a stacked centre, so a drag-follow makes no sense
      // (slides never move) — navigation is the tabs only.
      allowTouchMove: false,
      watchSlidesProgress: true,
      // Wrap at the ends so autoplay cycles, without loop's duplicate slides
      // (keeps slide index === thumb index for direct slideTo on hover).
      rewind: count > 1,
      resistanceRatio: 0,
      creativeEffect: {
        // Slides stay stacked (no movement); the incoming image is masked open
        // over the outgoing one via CSS (.is-revealing). The
        // ~imperceptible 0.999 opacity is load-bearing: a virtual-translate
        // effect ends the transition off its own transitionend, and a pure
        // [0,0,0]/opacity:1 never changes, so Swiper would lock.
        prev: { translate: [0, 0, 0], opacity: 0.999 },
        next: { translate: [0, 0, 0], opacity: 0.999 },
      },
      // Not Swiper's own pauseOnMouseEnter: it drives pause()/resume(), which this
      // slider cannot use (see syncAutoplayState below), and it would only cover the
      // media slider while the tabs beside it are as much part of the hover target.
      // The Pause on hover setting is wired through that same gate instead.
      autoplay: autoplay ? { delay, disableOnInteraction: false } : false,
      thumbs: { swiper: thumbsSwiper },
      on: {
        init() {
          syncTabs(this);
        },
        slideChange() {
          syncTabs(this);
        },
        slideChangeTransitionStart() {
          startCreativeReveal(this);
        },
        autoplayTimeLeft(s, time, progress) {
          if (holdTimer) return;
          // Autoplay is stopped and started again whenever a gate closes and opens
          // (hover, focus, the section leaving the viewport), and each start counts a
          // full delay from scratch — fed straight to the bar the fill would rewind to
          // empty. So a restart keeps what is on screen and spreads what is LEFT of
          // the bar over what is left of the timer: the fill only moves forward, at a
          // steady pace, and still lands full as the tab flips.
          const fraction = 1 - progress;
          if (fraction < lastTimerFraction) fillBase = fillValue;
          lastTimerFraction = fraction;
          fillValue = fillBase + (1 - fillBase) * fraction;
          fill(s.realIndex, fillValue);
        },
      },
    });

    // Autoplay is gated by three independent signals — off-screen (Intersection
    // Observer), keyboard focus inside the tabs row, and the pointer resting on the
    // slider when the merchant asked for it — any one of them halts it. A keyboard
    // user tabbing through the row needs it to fully stop advancing under them
    // (WCAG 2.2.2): focus already jumps the slide on focusin below, so autoplay
    // racing ahead mid-navigation would fight the user's own Tab presses. Uses
    // stop()/start(), NOT pause()/resume(): Swiper's autoplay pauses itself around
    // every transition (including ours) and silently resumes on that transition's
    // OWN transitionend via an internal closure reference our pause()/resume()
    // calls can't intercept — only the `running` flag stop()/start() toggle
    // actually short-circuits that.
    let sectionVisible = true;
    let tabsFocused = false;
    let pointerOver = false;
    const syncAutoplayState = () => {
      if (!autoplay || !mainSwiper.autoplay) return;
      if (sectionVisible && !tabsFocused && !pointerOver)
        mainSwiper.autoplay.start();
      else mainSwiper.autoplay.stop();
    };

    // Drive the main slider from the tabs ourselves (click + hover + focus). The
    // thumbs module only syncs the active class; keyboard activation is wired here
    // because the tab is a div[role=button] (it holds Heading/Description blocks).
    //
    // Hover waits for the pointer to settle: reaching the far tab means crossing
    // the ones between it, and switching on every one of those starts a reveal
    // that is cut short a frame later. Intent only gates HOVER — click, focus and keys
    // are deliberate and switch at once.
    const HOVER_INTENT_MS = 100;
    let hoverTimer = null;
    const cancelHoverIntent = () => {
      if (hoverTimer) clearTimeout(hoverTimer);
      hoverTimer = null;
    };
    const controller = new AbortController();

    // The one way into the slider.
    const goTo = (index) => {
      cancelHoverIntent();
      mainSwiper.slideTo(index);
    };

    tabButtons.forEach((btn, index) => {
      const go = () => goTo(index);
      btn.addEventListener("click", go, { signal: controller.signal });
      btn.addEventListener(
        "mouseenter",
        () => {
          cancelHoverIntent();
          hoverTimer = setTimeout(go, HOVER_INTENT_MS);
        },
        { signal: controller.signal }
      );
      btn.addEventListener("mouseleave", cancelHoverIntent, {
        signal: controller.signal,
      });
      btn.addEventListener("focusin", go, { signal: controller.signal });
      btn.addEventListener(
        "keydown",
        (event) => {
          if (
            event.key === "Enter" ||
            event.key === " " ||
            event.key === "Spacebar"
          ) {
            event.preventDefault();
            go();
          }
        },
        { signal: controller.signal }
      );
    });

    // Mobile: the tab strip is a slider of its own (slidesPerView 1.15), so the tab a
    // swipe settles on takes over by itself — landing a tab in the row reads as
    // choosing it, and a strip that scrolls without switching anything reads as dead.
    // The index guard is what keeps this from looping: the thumbs module moves the
    // strip on every change of the main slider too, and that lands on the index the
    // main slider is already on. From $md the whole row fits, the strip is locked and
    // this never fires.
    thumbsSwiper.on("slideChange", () => {
      const index = thumbsSwiper.activeIndex;
      if (index === mainSwiper.realIndex) return;
      goTo(index);
    });

    // The creative effect stacks the slides (translate [0,0,0]), so Swiper's own
    // drag is off: with nothing following the finger a slow drag reads as dead.
    // The gesture is recognised here instead — one swipe, or one trackpad flick,
    // moves exactly one tab whatever its speed. Same layer the slideshow uses
    // (section-slideshow.js) for the same reason.
    //
    // Direction comes from the gesture, NOT from comparing indexes: a flick
    // forward off the last tab wraps to the first (rewind), which as a mirrored
    // step would send the bar back along the row against the swipe.
    if (count > 1) {
      const SWIPE_DISTANCE = 40;
      const WHEEL_DISTANCE = 40;

      // In the Overlay layout the media IS the screen and the header + tab strip
      // layer over it, so the gesture belongs to the whole stage — a swipe across
      // the copy at the top has no media under it to land on otherwise.
      const gestureEl =
        root.dataset.layout === "overlay"
          ? root.querySelector(".tabs-slider__wrapper") || mediaEl
          : mediaEl;

      let startX = 0;
      let startY = 0;
      let tracking = false;
      let lastGesture = 0;

      const advance = (direction) => {
        const now = Date.now();
        // A reveal cut short is handled (settleReveal), but a queued flick still
        // reads as the section skipping tabs — one change per travel.
        if (now - lastGesture < SLIDE_SPEED) return false;
        lastGesture = now;
        const index = (mainSwiper.realIndex + direction + count) % count;
        goTo(index);
        return true;
      };

      const onPointerDown = (event) => {
        // The tab strip is a slider of its own — its swipe stays its own.
        if (event.target.closest(".tabs-slider__tabs")) return;
        tracking = true;
        startX = event.clientX;
        startY = event.clientY;
      };

      const onPointerUp = (event) => {
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        // Vertical travel belongs to the page — above all in Overlay, where the
        // stage is the full viewport and swallowing it would trap the scroll.
        if (Math.abs(dx) <= Math.abs(dy)) return;
        if (Math.abs(dx) < SWIPE_DISTANCE) return;
        advance(dx < 0 ? 1 : -1);
      };

      const onWheel = (event) => {
        if (Math.abs(event.deltaX) < WHEEL_DISTANCE) return;
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        if (advance(event.deltaX > 0 ? 1 : -1)) event.preventDefault();
      };

      gestureEl.addEventListener("pointerdown", onPointerDown, {
        signal: controller.signal,
      });
      gestureEl.addEventListener("pointerup", onPointerUp, {
        signal: controller.signal,
      });
      // The browser taking the gesture over for a scroll cancels the pointer, so
      // a vertical swipe never reaches pointerup with a stale start point.
      gestureEl.addEventListener(
        "pointercancel",
        () => {
          tracking = false;
        },
        { signal: controller.signal }
      );
      gestureEl.addEventListener("wheel", onWheel, {
        passive: false,
        signal: controller.signal,
      });
    }

    thumbsEl.addEventListener(
      "focusin",
      () => {
        tabsFocused = true;
        syncAutoplayState();
      },
      { signal: controller.signal }
    );
    thumbsEl.addEventListener(
      "focusout",
      (event) => {
        // Moving focus between tabs (Tab/Shift+Tab within the row) keeps it paused.
        if (thumbsEl.contains(event.relatedTarget)) return;
        tabsFocused = false;
        syncAutoplayState();
      },
      { signal: controller.signal }
    );

    // Pause on hover. Bound to the slider body, so the section heading is not part of
    // the target, and to pointer events with a mouse guard: a touch tap fires
    // pointerenter with no pointerleave to follow, which would strand autoplay stopped.
    if (autoplay && pauseOnHover) {
      const hoverTarget = root.querySelector(".tabs-slider__body") || root;
      const setPointerOver = (over) => (event) => {
        if (event.pointerType !== "mouse") return;
        pointerOver = over;
        syncAutoplayState();
      };
      hoverTarget.addEventListener("pointerenter", setPointerOver(true), {
        signal: controller.signal,
      });
      hoverTarget.addEventListener("pointerleave", setPointerOver(false), {
        signal: controller.signal,
      });
    }

    // Pause autoplay when the section is off-screen (parity with the slideshow).
    let observer = null;
    if (autoplay && mainSwiper.autoplay) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          sectionVisible = entry.isIntersecting;
          syncAutoplayState();
        });
      });
      observer.observe(section);
    }

    section._tabsMainSwiper = mainSwiper;

    section._tabsSliderCleanup = () => {
      controller.abort();
      cancelHoverIntent();
      clearTimeout(holdTimer);
      if (observer) observer.disconnect();
      if (mainSwiper && !mainSwiper.destroyed) mainSwiper.destroy(true, true);
      if (thumbsSwiper && !thumbsSwiper.destroyed)
        thumbsSwiper.destroy(true, true);
      section._tabsMainSwiper = null;
    };
  };

  const section = document.currentScript.parentElement;
  onNearViewport(section, () => initSection(section));

  if (!window.__themeTabsSliderEvents) {
    window.__themeTabsSliderEvents = true;

    document.addEventListener("shopify:section:load", (event) => {
      initSection(event.target);
    });

    document.addEventListener("shopify:section:unload", (event) => {
      const section = event.target;
      if (section && section._tabsSliderCleanup) {
        section._tabsSliderCleanup();
        section._tabsSliderCleanup = null;
      }
    });

    // Theme editor: jump to the slide of the block being edited (the block id is
    // on both its media slide and its tab slide — match either, then slideTo).
    document.addEventListener("shopify:block:select", (event) => {
      const slide = event.target.closest?.(
        ".tabs-slider__media-slide, .tabs-slider__tab-slide"
      );
      const section = event.target.closest?.(".tabs-slider-section");
      const swiper = section && section._tabsMainSwiper;
      if (!slide || !swiper || !slide.parentElement) return;
      const index = Array.from(slide.parentElement.children).indexOf(slide);
      if (index >= 0) swiper.slideTo(index);
    });
  }
})();
